import React, { memo, useCallback, useMemo } from 'react';
import { useRouteParam } from '@core/router';
import { Container, Row, Col, Card, CardBody } from 'reactstrap';
import { PageHeader, HeaderButtons } from '@modules/common';
import { useBloc } from '@core/utils/bloc';
import { useObservable } from '@core/utils/hooks/rxjs';
import { useLoadingState } from '@core/utils/repository/loading_state';
import { TopLoadingBar } from '@core/components/LoadingBar';
import { HelpArticlePageBloc } from './HelpArticlePageBloc';
import parse from 'html-react-parser';
import { useCompany } from '@core/utils/hooks/misc';
import { openAddEditHelpArticleModal } from '../AddEditHelpArticleModal';

export const HelpArticlePage = memo(() => {
  const helpArticleId = useRouteParam('id');
  const bloc = useBloc(HelpArticlePageBloc, helpArticleId);
  const helpArticle = useObservable(bloc.helpArticle$);
  const loading = useLoadingState(bloc.loading);
  const company = useCompany();

  const onView = useCallback(
    (itemSlug: string, categorySlug: string | undefined) => {
      categorySlug = categorySlug ? `${categorySlug}` : 'uncategorized';
      const link = `/public/${company.slug}/${categorySlug}/${itemSlug}`;
      window.open(link, '_blank');
    },
    [company],
  );

  const buttons: HeaderButtons[] = useMemo(() => {
    if (!helpArticle) return [];
    return [
      {
        title: 'View',
        intent: 'dark',
        onClick: () => onView(helpArticle.slug, helpArticle.categorySlug),
      },
      {
        title: 'Edit',
        onClick: () => openAddEditHelpArticleModal({ helpArticle }),
      },
    ];
  }, [helpArticle, onView]);

  if (loading.loading || !helpArticle) return <TopLoadingBar />;

  return (
    <div className="page-content">
      <Container fluid>
        <PageHeader buttons={buttons}>{helpArticle.title}</PageHeader>
        <Row>
          <Col className="col-12">
            <Card>
              <CardBody>{parse(helpArticle.content || '')}</CardBody>
            </Card>
          </Col>
        </Row>
      </Container>
    </div>
  );
});
